/**
 * NHL Odds Modal Component
 *
 * Displays detailed betting odds for NHL games
 * Adapted from NFL odds modal
 *
 * TO BE IMPLEMENTED: Awaiting NHL API and odds API integration
 */

import React from 'react';

/**
 * NHL Odds Modal
 *
 * Displays:
 * - Moneyline odds
 * - Puck line (spread)
 * - Over/Under totals
 * - Period props
 * - Player props
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Modal visibility
 * @param {Function} props.onClose - Close handler
 * @param {Object} props.game - Game data
 * @returns {JSX.Element}
 */
const NHLOddsModal = ({ isOpen = true, onClose, game }) => {
  if (!isOpen || !game) return null;

  // TO BE IMPLEMENTED: Fetch odds data from API

  return (
    <div className="nhl-odds-modal fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="modal-content bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="modal-header sticky top-0 bg-white dark:bg-gray-800 border-b p-4 flex justify-between items-center">
          <h2 className="text-xl font-bold">Betting Odds</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div className="modal-body p-6">
          {/* Game Info */}
          <div className="game-info mb-6">
            <p className="text-sm text-gray-500 mb-2">Game Matchup</p>
            <div className="text-lg font-semibold">
              {/* TO BE IMPLEMENTED: Team names from game data */}
              Away Team @ Home Team
            </div>
          </div>

          {/* Odds Sections */}
          <div className="odds-sections space-y-6">
            {/* Moneyline */}
            <div className="odds-section">
              <h3 className="text-md font-semibold mb-3">Moneyline</h3>
              <div className="odds-grid bg-gray-50 dark:bg-gray-900 p-4 rounded">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bookmaker-odds">
                    <p className="text-xs text-gray-500 mb-1">Away</p>
                    <p className="text-lg font-bold">+130</p>
                  </div>
                  <div className="bookmaker-odds">
                    <p className="text-xs text-gray-500 mb-1">Home</p>
                    <p className="text-lg font-bold">-150</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Puck Line */}
            <div className="odds-section">
              <h3 className="text-md font-semibold mb-3">Puck Line</h3>
              <div className="odds-grid bg-gray-50 dark:bg-gray-900 p-4 rounded">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bookmaker-odds">
                    <p className="text-xs text-gray-500 mb-1">Away +1.5</p>
                    <p className="text-lg font-bold">-220</p>
                  </div>
                  <div className="bookmaker-odds">
                    <p className="text-xs text-gray-500 mb-1">Home -1.5</p>
                    <p className="text-lg font-bold">+180</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Totals (Over/Under) */}
            <div className="odds-section">
              <h3 className="text-md font-semibold mb-3">Total Goals</h3>
              <div className="odds-grid bg-gray-50 dark:bg-gray-900 p-4 rounded">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bookmaker-odds">
                    <p className="text-xs text-gray-500 mb-1">Over 6.5</p>
                    <p className="text-lg font-bold">-110</p>
                  </div>
                  <div className="bookmaker-odds">
                    <p className="text-xs text-gray-500 mb-1">Under 6.5</p>
                    <p className="text-lg font-bold">-110</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Period Props */}
            <div className="odds-section">
              <h3 className="text-md font-semibold mb-3">Period Props</h3>
              <div className="odds-grid bg-gray-50 dark:bg-gray-900 p-4 rounded">
                <p className="text-sm text-gray-500">
                  Period-specific betting - Awaiting API integration
                </p>
              </div>
            </div>

            {/* Player Props */}
            <div className="odds-section">
              <h3 className="text-md font-semibold mb-3">Player Props</h3>
              <div className="odds-grid bg-gray-50 dark:bg-gray-900 p-4 rounded">
                <p className="text-sm text-gray-500">
                  Player props (goals, assists, shots) - Awaiting API integration
                </p>
              </div>
            </div>
          </div>

          {/* API Notice */}
          <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded">
            <p className="text-sm text-blue-800 dark:text-blue-200">
              Odds data is placeholder. Awaiting NHL odds API integration.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="modal-footer border-t p-4 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default NHLOddsModal;
